import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { StorefrontIcon as Storefront, ArrowRightIcon as ArrowRight, WarningIcon as Warning, CheckCircleIcon as CheckCircle, XIcon as X } from "@phosphor-icons/react";

/**
 * Admin-only bulk reassignment for storefronts uploaded to the wrong
 * account.
 *
 * Use case (founder, 2026-05-29): "I built food listings on my admin
 * account but they should live on a clean Supreme Team K9 seller
 * account." We move ownership of the entire `stores` row + its
 * children stay attached (they're keyed by store_id, not user_id).
 *
 * Flow:
 *   1. Page mounts → list every store where owner_user_id == admin
 *   2. Admin types destination email + selects stores
 *   3. Confirm modal → POST /admin/storefronts-reassign/reassign-bulk
 *   4. Per-store result strip + payout warning if dest has no Connect
 *
 * Safety: backend validates destination exists, current ownership
 * matches the declared source, and writes a `store_audit_log` entry
 * per move so the trail isn't broken.
 */
export default function AdminStorefrontReassign() {
  const [stores, setStores] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [destEmail, setDestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    api.get("/admin/storefronts-reassign/owned-by-me")
      .then((r) => setStores(r.data?.stores || []))
      .catch((e) => {
        toast.error(formatApiError(e));
        setStores([]);
      });
  };
  useEffect(load, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set((stores || []).map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/storefronts-reassign/reassign-bulk", {
        store_ids: Array.from(selected),
        destination_email: destEmail.trim(),
      });
      setResult(data);
      setConfirming(false);
      toast.success(`Moved ${data.moved_count} of ${selected.size} storefronts.`);
      load();
      clearAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!stores) {
    return <AppShell><div className="max-w-4xl mx-auto px-4 py-10">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-storefront-reassign">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight">Bulk reassign storefronts</h1>
          <p className="text-[13px] sm:text-sm text-[#6B6B6B] mt-1 max-w-2xl">
            Move The Dog Shop storefronts currently owned by the admin
            account onto a real seller account. Products and orders stay
            attached (they're keyed by store, not user); an audit-log entry
            is written for each move. Stores owned by anyone else are
            silently skipped.
          </p>
        </header>

        {stores.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-[#EAEAEA] p-8 text-center" data-testid="admin-storefront-reassign-empty">
            <CheckCircle size={28} weight="duotone" className="text-[#0F8043] mx-auto mb-2" />
            <h3 className="font-spectral text-lg">No storefronts to reassign</h3>
            <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-md mx-auto">
              The admin account doesn't currently own any Dog Shop storefronts.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border border-[#EAEAEA] bg-white p-4 sm:p-5 mb-5">
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#6B6B6B] block mb-1">
                Destination seller email
              </label>
              <Input
                value={destEmail}
                onChange={(e) => setDestEmail(e.target.value)}
                placeholder="seller@supremeteamk9.com"
                type="email"
                data-testid="admin-storefront-reassign-dest-email"
                className="font-tabular"
              />
              <p className="text-[11px] text-[#6B6B6B] mt-2">
                The destination user must already be registered. For payouts
                to work on future orders, they also need a completed Stripe
                Connect onboarding — you'll be warned if not.
              </p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <p className="text-[13px] text-ink">
                <strong>{selected.size}</strong> of {stores.length} selected
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAll} className="rounded-full" data-testid="admin-storefront-reassign-select-all">
                  Select all
                </Button>
                {selected.size > 0 && (
                  <Button size="sm" variant="outline" onClick={clearAll} className="rounded-full" data-testid="admin-storefront-reassign-clear">
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <ul className="space-y-2 mb-6" data-testid="admin-storefront-reassign-list">
              {stores.map((s) => {
                const isSel = selected.has(s.id);
                return (
                  <li
                    key={s.id}
                    onClick={() => toggle(s.id)}
                    className={`bg-white border-2 rounded-xl p-3 sm:p-4 flex items-center gap-3 cursor-pointer transition-colors ${
                      isSel ? "border-crimson" : "border-[#EAEAEA] hover:border-ink/30"
                    }`}
                    data-testid={`admin-storefront-reassign-store-${s.id}`}
                  >
                    <Checkbox checked={isSel} onCheckedChange={() => toggle(s.id)} />
                    <span className="p-2 rounded-full bg-[#FBF7EC]">
                      <Storefront size={16} weight="duotone" className="text-gold-dark" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-ink truncate">
                        {s.name || "(unnamed)"}
                        <span className="text-[11px] text-[#6B6B6B] font-normal ml-2">/store/{s.slug}</span>
                      </p>
                      <p className="text-[11px] text-[#6B6B6B] truncate">
                        {s.product_count} product{s.product_count === 1 ? "" : "s"} ·
                        {" "}{s.order_count} order{s.order_count === 1 ? "" : "s"}
                        {s.status && s.status !== "active" && (
                          <span className="ml-2 uppercase tracking-wider text-crimson">{s.status}</span>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="sticky bottom-4 bg-white border-2 border-ink rounded-2xl p-4 shadow-lg flex items-center justify-between flex-wrap gap-3">
              <div className="text-[12.5px] min-w-0">
                {selected.size === 0
                  ? <span className="text-[#6B6B6B]">Select storefronts to move</span>
                  : <span><strong>{selected.size}</strong> store{selected.size === 1 ? "" : "s"} → <strong className="text-crimson">{destEmail || "(no destination)"}</strong></span>}
              </div>
              <Button
                onClick={() => setConfirming(true)}
                disabled={selected.size === 0 || !destEmail.trim() || busy}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="admin-storefront-reassign-submit-btn"
              >
                Reassign selected <ArrowRight size={13} weight="bold" className="ml-1" />
              </Button>
            </div>
          </>
        )}

        {confirming && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-storefront-reassign-confirm-modal">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-start gap-3 mb-3">
                <Warning size={22} weight="fill" className="text-crimson mt-0.5" />
                <h3 className="font-spectral text-xl tracking-tight">Confirm storefront reassignment</h3>
              </div>
              <p className="text-sm text-[#3F3F3F] mb-4">
                Move <strong>{selected.size}</strong> storefront{selected.size === 1 ? "" : "s"} (and every product + order under them) from the admin account to <strong>{destEmail}</strong>?
              </p>
              <p className="text-[12px] text-[#6B6B6B] mb-4">
                The destination user takes over The Dog Shop subscription scope and Stripe Connect routing for any future orders. Past orders that already settled aren't affected.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy} className="rounded-full">
                  Cancel
                </Button>
                <Button onClick={submit} disabled={busy} className="rounded-full bg-crimson hover:bg-ink text-white" data-testid="admin-storefront-reassign-confirm-btn">
                  {busy ? "Moving…" : "Yes, reassign"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {result && (
          <section className="mt-6 rounded-2xl border border-[#BFE8C9] bg-[#EEFBEC] p-5" data-testid="admin-storefront-reassign-result">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle size={20} weight="fill" className="text-[#0F8043] mt-0.5" />
              <div>
                <h3 className="font-spectral text-lg tracking-tight">Done</h3>
                <p className="text-[13px] text-[#3F3F3F]">
                  Moved <strong>{result.moved_count}</strong>
                  {result.skipped_count > 0 && <> · skipped <strong>{result.skipped_count}</strong></>}
                </p>
              </div>
            </div>
            {result.payout_warning && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 mb-3 flex items-start gap-2" data-testid="admin-storefront-reassign-payout-warning">
                <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                <p className="text-[12px] text-amber-900">{result.payout_warning}</p>
              </div>
            )}
            {result.moved.length > 0 && (
              <ul className="space-y-1 text-[12.5px] mb-3">
                {result.moved.map((m) => (
                  <li key={m.store_id} className="flex items-center gap-2 text-[#0F8043]">
                    <CheckCircle size={12} weight="fill" /> {m.store_name || m.store_id}
                    {m.slug && <span className="text-[#6B6B6B]">/store/{m.slug}</span>}
                  </li>
                ))}
              </ul>
            )}
            {result.skipped.length > 0 && (
              <details className="text-[12px]">
                <summary className="text-[#6B6B6B] cursor-pointer hover:text-ink">
                  Skipped ({result.skipped.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {result.skipped.map((s) => (
                    <li key={s.store_id} className="flex items-start gap-2 text-[#6B6B6B]">
                      <X size={12} weight="bold" className="mt-0.5 shrink-0" />
                      <span>{s.store_name || s.store_id} — {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
